import OnboardingTip from '@rio-cloud/rio-uikit/OnboardingTip';
import useLocalStorage from '@rio-cloud/rio-uikit/useLocalStorage';
import { ForwardedRef, FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { FeatureToggles, useFeatureToggle } from '../../../../configuration/featureToggle/toggleHook';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import {
    getSelectedCategory,
    getSortingForCategory,
    transportAssignmentsSlice,
} from '../../reducers/transportAssignmentsSlice';
import {
    ColumnDefinition,
    ColumnTableHeader,
    getSortDir,
    SortColumnTableHeaderFactory,
} from '../../sharedComponents/table/SortedTableHeader';
import { TableCategory } from '../tableCategory.types';
import { TransportAssignmentTableColumn } from './TransportAssignmentTableColumns';
import classNames from 'classnames';

const SortColumnTableHeader = SortColumnTableHeaderFactory<TransportAssignmentTableColumn>();

type TransportAssignmentTableHeaderProps = {
    columnDefinition: ColumnDefinition<TransportAssignmentTableColumn>[];
    selectionHeader?: ReactNode;
};

const MyOnboardingTip = (
    props: PropsWithChildren<{ show: boolean; onHide: () => void; titleKey: string; contentKeys: string[] }>,
) => {
    const intl = useIntl();

    return (
        <OnboardingTip
            className="z-index-10"
            show={props.show}
            placement={OnboardingTip.BOTTOM_END}
            width={250}
            title={intl.formatMessage({ id: props.titleKey })}
            content={props.contentKeys.map((key) => {
                return <p key={key}>{intl.formatMessage({ id: key })}</p>;
            })}
            onHide={props.onHide}
        >
            {props.children}
        </OnboardingTip>
    );
};

const LocalSortColumn = ({
    def,
    childClassNames,
    ref,
}: {
    def: ColumnDefinition<TransportAssignmentTableColumn>;
    childClassNames?: classNames.Argument;
    ref?: ForwardedRef<HTMLTableCellElement>;
}) => {
    const dispatch = useAppDispatch();
    const activeTableCategory = useAppSelector(getSelectedCategory);
    const sorting = useAppSelector(getSortingForCategory(activeTableCategory));
    const handleSortByColumn = (col: TransportAssignmentTableColumn) =>
        dispatch(
            transportAssignmentsSlice.actions.setSortingForCategory({
                category: activeTableCategory,
                sort: { column: col, dir: getSortDir(col, sorting) },
            }),
        );

    return (
        <SortColumnTableHeader
            col={def.column}
            labelId={def.labelId}
            sortingHandler={handleSortByColumn}
            currentSort={sorting}
            childClassNames={childClassNames}
            ref={ref}
        />
    );
};

// eslint-disable-next-line max-lines-per-function
export const TransportAssignmentTableHeader: FunctionComponent<TransportAssignmentTableHeaderProps> = ({
    columnDefinition,
    selectionHeader,
}) => {
    const activeTableCategory = useAppSelector(getSelectedCategory);
    const trackingFeatureEnabled = useFeatureToggle(FeatureToggles.TRACKING, false).value;

    const [showOnboarding1, setShowOnboarding1] = useLocalStorage('onboarding.date.1', 'true');
    const [showOnboarding2, setShowOnboarding2] = useLocalStorage('onboarding.date.2', 'true');
    const [showOnboarding3, setShowOnboarding3] = useLocalStorage('onboarding.date.3', 'true');
    const [showOnboarding4, setShowOnboarding4] = useLocalStorage('onboarding.date.4', 'true');

    const isTrueOnboarding1 = () => showOnboarding1 === 'true';
    const isTrueOnboarding2 = () => !isTrueOnboarding1() && showOnboarding2 === 'true';
    const isTrueOnboarding3 = () => !isTrueOnboarding1() && !isTrueOnboarding2() && showOnboarding3 === 'true';
    const isTrueOnboarding4 = () =>
        !isTrueOnboarding1() && !isTrueOnboarding2() && !isTrueOnboarding3() && showOnboarding4 === 'true';

    // eslint-disable-next-line max-lines-per-function
    const renderHeaderColumn = (def: ColumnDefinition<TransportAssignmentTableColumn>) => {
        switch (def.column) {
            case TransportAssignmentTableColumn.MODEL:
            case TransportAssignmentTableColumn.MODEL_GROUP:
            case TransportAssignmentTableColumn.ORDER_ISSUE_DATE:
            case TransportAssignmentTableColumn.HAS_CHANGES:
            case TransportAssignmentTableColumn.MEANS_OF_TRANSPORT:
                return <ColumnTableHeader key={def.column} labelId={def.labelId} />;
            case TransportAssignmentTableColumn.EXTERNAL_ID:
            case TransportAssignmentTableColumn.FROM_LOCATION_ID:
            case TransportAssignmentTableColumn.TO_LOCATION_ID:
                return (
                    <ColumnTableHeader
                        key={def.column}
                        additionalClassNames="white-space-normal"
                        labelId={def.labelId}
                    />
                );
            case TransportAssignmentTableColumn.STATUS:
                return trackingFeatureEnabled ? (
                    <LocalSortColumn key={def.column} def={def} childClassNames="margin-left-25" />
                ) : (
                    <LocalSortColumn key={def.column} def={def} />
                );
            case TransportAssignmentTableColumn.FROM:
            case TransportAssignmentTableColumn.TO:
                return <LocalSortColumn key={def.column} def={def} />;
            case TransportAssignmentTableColumn.REQUESTED_PICKUP:
                return (
                    <MyOnboardingTip
                        key={def.column}
                        show={isTrueOnboarding1()}
                        titleKey="outboundOrderBook.common.onboarding.date.title"
                        contentKeys={['outboundPortal.notification.onboarding.date.content']}
                        onHide={() => setShowOnboarding1('false')}
                    >
                        <LocalSortColumn def={def} />
                    </MyOnboardingTip>
                );
            case TransportAssignmentTableColumn.REQUESTED_DELIVERY:
                return (
                    <MyOnboardingTip
                        key={def.column}
                        show={isTrueOnboarding2()}
                        titleKey="outboundOrderBook.common.onboarding.date.title"
                        contentKeys={['outboundPortal.notification.onboarding.date.content']}
                        onHide={() => setShowOnboarding2('false')}
                    >
                        <LocalSortColumn def={def} />
                    </MyOnboardingTip>
                );
            case TransportAssignmentTableColumn.PICKUP:
                return (
                    <MyOnboardingTip
                        key={def.column}
                        show={isTrueOnboarding3()}
                        titleKey={def.labelId!}
                        contentKeys={[
                            'outboundOrderBook.transportAssignments.table.onboarding.pickup.content',
                            'outboundPortal.notification.onboarding.date.content',
                        ]}
                        onHide={() => setShowOnboarding3('false')}
                    >
                        <LocalSortColumn def={def} />
                    </MyOnboardingTip>
                );
            case TransportAssignmentTableColumn.DELIVERY:
                return (
                    <MyOnboardingTip
                        key={def.column}
                        show={isTrueOnboarding4()}
                        titleKey={def.labelId!}
                        contentKeys={[
                            'outboundOrderBook.transportAssignments.table.onboarding.delivery.content',
                            'outboundPortal.notification.onboarding.date.content',
                        ]}
                        onHide={() => setShowOnboarding4('false')}
                    >
                        <LocalSortColumn def={def} />
                    </MyOnboardingTip>
                );
            case TransportAssignmentTableColumn.ACTION:
                return activeTableCategory !== TableCategory.ARCHIVE ? (
                    <ColumnTableHeader key={def.column} labelId={def.labelId} />
                ) : null;
        }
    };

    return (
        <thead>
            <tr>
                {selectionHeader && <th className="table-checkbox">{selectionHeader}</th>}
                {columnDefinition.map((def) => renderHeaderColumn(def))}
            </tr>
        </thead>
    );
};
